import { h, Component } from 'preact';
import styles from './styles.scss';
import { Link } from '../../shared/link';
import { TopicFlyout } from '../../shared/topic-flyout';
import { Flyout, FlyoutToggle } from '../../shared/flyout';
import { breakpointLarge } from '../../sass/variables.scss';
import { UseCaretIcon } from '../../shared/svg/icons/caret';
import { withTheme } from '../../shared/withTheme';
import { I18n, Config } from '../../shared/context';
import { Games } from '../../shared/svg/logos/games';
import parse from 'html-react-parser';
import { BadgeSVG } from '../../shared/svg/badges';
import { sanitizeHtml } from '@buzzfeed/bf-utils/lib/sanitize-html';

function enableFlyout() {
  const { matches } = window.matchMedia(`(min-width:${breakpointLarge})`);
  return !!matches;
}

const TopicLink = withTheme(({ theme, flyout = null, ...props }) => {
  if (flyout) {
    return (
      <Config.Consumer>
        {config => (
          <Flyout width={420} enabled={enableFlyout} location={'topicBar'} label={props.label} ga={config.ga}>
            <FlyoutToggle>
              <Link location={'topicBar'} className={`${styles.link} ${theme.link}`} {...props}>
                {props.emoji} {props.children}
                <UseCaretIcon className={styles.downCaret} aria-hidden={true} />
              </Link>
            </FlyoutToggle>
            <TopicFlyout location={props.label} {...flyout} />
          </Flyout>
        )}
      </Config.Consumer>
    );
  }
  return <Link location={'topicBar'} className={`${styles.link} ${theme.link} ${props.mobileOnly ? styles.mdHide : ''}`} {...props} />;
});

class TopicNav extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayedItems: [...props.topics],
      dailyFact: null,
      isSticking: props.isSticking,
      isSecondNav: props.isSecondNav
    };
  }

  updateDailyFact = (event) => {
    const sameOrigin = event?.origin === window.location.origin;
    if (!sameOrigin) {
      return;
    }

    const factTypeOk = event?.data?.type === 'fact-of-the-day';
    const incoming = event?.data?.message;
    if (!factTypeOk || !incoming?.text) {
      return;
    }

    const sanitizedHTML = sanitizeHtml(incoming.text);

    this.setState({
      ...this.state,
      dailyFact: {
        text: sanitizedHTML,
        url: incoming.url,
        forceNewBrowserTab: !!incoming.forceNewBrowserTab
      }
    });
  }

  componentDidMount() {
    // Wrap to keep a stable reference for removal
    this._messageHandler = (e) => this.updateDailyFact(e);
    window.addEventListener('message', this._messageHandler, false);

    const targetOrigin = window.location.origin;
    window.parent.postMessage(
      { type: 'topic-nav-loaded' },
      targetOrigin
    );
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isSticking !== this.props.isSticking) {
      this.setState({ isSticking: this.props.isSticking });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this._messageHandler, false);
  }

  getLogo(name) {
    if (name === 'Games') {
      return <Games width={25} height={26} aria-hidden={true} />;
    }

    return null;
  }

  render({ theme }, state) {
    /* eslint-disable new-cap */
    return <I18n.Consumer>
      { i18n =>
        <div className={`${state.isSecondNav ? theme.secondSubNavContainer : ''} ${styles.subNavContainer} ${state.isSticking ? styles.isSticking : ''} ${state.isSecondNav ? styles.isSecondNav : styles.isPrimaryNav}`} >
          <nav className={`${theme.topicNavWrapper} ${styles.wrapper}`} aria-label={i18n.hot_topics}>
            <div className={`${styles.bfLogo} ${theme.secondNavLogo}`}>
              <Config.Consumer>{ ({ brand_urls }) => (
                <Link href={brand_urls.default.bfo} label={'buzzfeed'} location={'logo'}>
                  <BadgeSVG badgeName="trending" brand='buzzfeed' role="img" aria-labelledby="buzzfeed">
                    <title id="buzzfeed">BuzzFeed</title>
                  </BadgeSVG>
                </Link>
              )}</Config.Consumer>
            </div>
            <ul className={`${theme.topicNav} ${styles.topicNav}`} ref={el => this.element = el}>
              {
                state.displayedItems.map(({ url, name, label, ...data }) => (
                  <li className={`${styles.topicNavItem} ${this.getLogo(name) ? styles.topicNavItemWithLogo : ''} ${data.isMobile ? styles.mobileItem : styles.desktopItem}`} key={name}>
                    <TopicLink href={url} label={label || name} {...data}>{this.getLogo(name)}{name}</TopicLink>
                  </li>
                ))
              }
            </ul>
            {!!state.dailyFact?.text?.length && !state.dailyFact?.url?.length &&
              <span
                className={`${styles.dailyFact} ${state.isSticking ? styles.isSticking : ''}`}
                id='daily-fact'
                // sanitized HTML safe to inject
                dangerouslySetInnerHTML={{ __html: state.dailyFact.text }}
              />}
            {!!state.dailyFact?.text?.length && !!state.dailyFact?.url?.length && (
              <Link
                className={`${styles.dailyFactLink} ${state.isSticking ? styles.isSticking : ''}`}
                id='daily-fact'
                href={state.dailyFact.url}
                location={'dailyFact'}
                label={'daily_fact'}
                target={state.dailyFact?.forceNewBrowserTab ? '_blank' : '_self'}
                trackingData={{
                  position_in_unit: state?.displayedItems.length
                }}>
                {parse(state.dailyFact.text)}
              </Link>
            )}
          </nav>
        </div>
      }
    </I18n.Consumer>;
    /* eslint-enable new-cap */
  }
}

export const ThemedTopicNav = withTheme(TopicNav);
